import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { getMeeting, joinMeetingAsProvider } from '../services/api';

/**
 * MeetingEntry - handles direct meeting code URLs
 *
 * Patient: meetyourcare.com/JOHN-1234 -> enters name -> goes to waiting room
 * Provider: meetyourcare.com/JOHN-1234?token=XXX -> validates token -> goes directly to call
 */
function MeetingEntry() {
  const { code } = useParams<{ code: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Provider access requires a valid token (not just ?provider=true)
  const providerToken = searchParams.get('token');

  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [meeting, setMeeting] = useState<{
    meetingCode: string;
    status: string;
    agentName: string;
    patientName?: string;
  } | null>(null);

  // Validate meeting on load
  useEffect(() => {
    if (!code) {
      navigate('/');
      return;
    }

    const validateMeeting = async () => {
      try {
        // Pass token to API for provider validation
        const result = await getMeeting(code.toUpperCase(), providerToken || undefined);

        if (!result.success) {
          setError(result.error || 'Meeting not found');
          setIsLoading(false);
          return;
        }

        if (result.meeting?.status === 'expired' || result.meeting?.status === 'ended') {
          setError('This meeting has ended or expired');
          setIsLoading(false);
          return;
        }

        setMeeting(result.meeting || null);

        // If valid provider token, join the meeting and go directly to call
        if (providerToken && result.isValidProvider && result.meeting?.agentId) {
          // Join the meeting to get Chime credentials
          const joinResult = await joinMeetingAsProvider(
            code.toUpperCase(),
            result.meeting.agentName || 'Provider',
            result.meeting.agentId
          );

          if (!joinResult.success || !joinResult.meeting || !joinResult.attendee) {
            setError(joinResult.error || 'Failed to join meeting');
            setIsLoading(false);
            return;
          }

          // Store meeting info for VideoCall component
          sessionStorage.setItem('meetingInfo', JSON.stringify({
            meeting: joinResult.meeting,
            attendee: joinResult.attendee,
          }));
          sessionStorage.setItem('participantType', 'provider');
          sessionStorage.setItem('providerName', result.meeting.agentName || 'Provider');
          navigate(`/call/${code.toUpperCase()}`);
          return;
        }

        // If token was provided but invalid, show error
        if (providerToken && !result.isValidProvider) {
          setError('Invalid or expired access link');
          setIsLoading(false);
          return;
        }

        // For patient (no token), show name entry form
        setIsLoading(false);
      } catch (err) {
        console.error('Error validating meeting:', err);
        setError('Unable to connect. Please check your internet connection.');
        setIsLoading(false);
      }
    };

    validateMeeting();
  }, [code, providerToken, navigate]);

  const handleJoin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    // Store name and go to waiting room
    sessionStorage.setItem('patientName', name.trim());
    sessionStorage.setItem('participantType', 'patient');
    navigate(`/waiting/${code?.toUpperCase()}`);
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-slate-600 mt-4">
            {providerToken ? 'Verifying access...' : 'Loading meeting...'}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-slate-800 mb-2">Unable to Join</h1>
          <p className="text-slate-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Another Code
          </button>
        </div>
      </div>
    );
  }

  // Patient name entry form
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-slate-800">Join Video Call</h1>
          <p className="text-slate-600 mt-2">
            Meeting with <span className="font-medium">{meeting?.agentName || 'your provider'}</span>
          </p>
          <p className="text-sm text-slate-500 mt-1 font-mono">{code?.toUpperCase()}</p>
        </div>

        {/* Name Entry Form */}
        <form onSubmit={handleJoin} className="bg-white rounded-xl shadow-lg p-6 space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">
              Your Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError('');
              }}
              placeholder="Enter your name"
              className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              autoComplete="name"
              autoFocus
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Join Waiting Room
          </button>
        </form>

        <p className="text-center text-sm text-slate-500 mt-6">
          Your provider will join shortly after you enter the waiting room.
        </p>
      </div>
    </div>
  );
}

export default MeetingEntry;
